import { Inventory } from "src/utils/interfaces/interfaces";
import { FaLock, FaLockOpen } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { BatteryProgress } from "./BatteryProgress";

export interface IShipperOrdersProps {
  inventory: Inventory;
  onUnlock: Function;
}

export function InventoryList({
  inventory,
  onUnlock,
}: {
  inventory: Inventory;
  onUnlock?: () => void;
}) {
  const { t } = useTranslation();
  const [chargingProgress, setChargingProgress] = useState(0);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (inventory?.batteryLevel === 255) {
      interval = setInterval(() => {
        setChargingProgress((prevProgress) =>
          prevProgress >= 100 ? 0 : prevProgress + 10
        );
      }, 400);
    }
    return () => {
      clearInterval(interval);
    };
  }, [inventory?.batteryLevel]);

  const locationText = inventory.location
    ? inventory.location.length > 44
      ? `${inventory.location.substring(0, 44)}...`
      : inventory.location
    : "N/A";

  return (
    <div
      className="grid grid-cols-12 items-center gap-6 border-b px-8 py-6 last:border-b-0"
      style={{ borderColor: "var(--border-light)" }}
      key={inventory._id}
    >
      <div className="col-span-1 flex justify-center">
        <div
          className="flex h-12 w-12 items-center justify-center rounded-2xl"
          style={{ backgroundColor: "var(--bg-surface-2)" }}
        >
          <FaLock className="text-lg" style={{ color: "var(--color-primary)" }} />
        </div>
      </div>
      <div className="col-span-3">
        <p className="mb-1 text-xs" style={{ color: "var(--text-muted)" }}>IMEI</p>
        <div>
          <p className="text-xl font-semibold">{inventory.imei}</p>
        </div>
      </div>
      <div className="col-span-2">
        <p className="mb-1 text-xs" style={{ color: "var(--text-muted)" }}>Latitude</p>
        <div>
          <p className="text-base">
            {inventory.latitude && inventory.longitude
              ? `${inventory.latitude}, ${inventory.longitude}`
              : "N/A"}
          </p>
        </div>
      </div>
      <div className="col-span-2">
        <p className="mb-1 text-xs" style={{ color: "var(--text-muted)" }}>Location</p>
        <div>
          <p
            className="text-base"
            data-tooltip-id="tooltip"
            data-tooltip-content={inventory.location}
          >
            {locationText}
          </p>
        </div>
      </div>
      <div className="col-span-2">
        <p className="mb-1 text-xs" style={{ color: "var(--text-muted)" }}>Battery level</p>
        <div className="relative flex items-center h-full">
          {inventory.batteryLevel !== undefined &&
            inventory.batteryLevel !== null ? (
            inventory.batteryLevel === 255 ? (
              <BatteryProgress level={undefined} value={chargingProgress} text="Charging" />
            ) : (
              <>
                <BatteryProgress level={inventory.batteryLevel} value={inventory.batteryLevel} />
                <p className="ml-2 text-sm">{inventory.batteryLevel}%</p>
              </>
            )
          ) : (
            <p className="text-base">N/A</p>
          )}
        </div>
      </div>
      <div className="col-span-2 flex justify-end">
        <button
          type="button"
          className="flex items-center gap-2 rounded-lg px-4 py-2 font-semibold transition-colors duration-200"
          style={{
            backgroundColor: "var(--color-primary)",
            color: "var(--text-invert)",
          }}
          data-tooltip-id="tooltip"
          data-tooltip-content={t("button.unlock")}
          onClick={onUnlock}
        >
          <FaLockOpen className="text-base" />
          <span>{t("button.unlock")}</span>
        </button>
      </div>
    </div>
  );
}
